import {IFirebaseTopics, IFirebaseService} from "./firebase.service.interface";
import {ServiceBase} from "../ServiceBase";
import {Triple8ConnectionFactory} from "./triple8/triple8-connection-factory";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {BankrollUpdatesTopicModel} from "./topics/bankroll/bankroll-updates-topic.model";
import {IFirebaseTopicModel} from "./topics/firebase-topic-model.interface";
import {ITriple8Connection} from "./triple8/triple8-connection.inteface";
import {MessagesTopicModel} from "./topics/messages/messages-topic.model";
import {FreePlayTopicModel} from "./topics/free-play/free-play-topic.model";
import {GamblingSessionTopicModel} from "./topics/gambling-session/gambling-session-topic.model";
import {JackpotBetsFeedsTopicModel} from "./topics/jackpot-bets/jackpot-bets-feeds-topic.model";
import {JackpotBetsErrorsTopicModel} from "./topics/jackpot-bets/jackpot-bets-errors-topic.model";
import {JackpotBetsWinsTopicModel} from "./topics/jackpot-bets/jackpot-bets-wins-topic.model";
import {NetPositionTopicModel} from "./topics/net-position/net-position-topic.model";
import {FirebaseAnalyticsReporter} from "./firebase-analytics-reporter";

interface ILastFirebaseToken {
    token: string;
    timestamp: number;
}
export class FirebaseService extends ServiceBase implements IFirebaseService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.analytics = new FirebaseAnalyticsReporter(services);

        this.topics = {
            bankroll: new BankrollUpdatesTopicModel(services, this._getConnection),
            messages: new MessagesTopicModel(this.services, this._getConnection),
            freePlay: new FreePlayTopicModel(services, this._getConnection),
            gamblingSession: new GamblingSessionTopicModel(services, this._getConnection),
            jackpotBetsFeeds: new JackpotBetsFeedsTopicModel(services, this._getConnection),
            jackpotBetsErrors: new JackpotBetsErrorsTopicModel(services, this._getConnection),
            jackpotBetsWins: new JackpotBetsWinsTopicModel(services, this._getConnection),
            netPosition: new NetPositionTopicModel(services, this._getConnection),
        };

        this.reaction(() => this.services.user.isAuthorized,
            async (isAuthorized) => {
                if(isAuthorized) {
                    this._subscribeUnsubscribeQueue.enqueue(() => this._subscribeToTopics());
                } else {
                    this._subscribeUnsubscribeQueue.enqueue(() => this._unsubscribeFromTopics());
                }
        }, {
            fireImmediately: true
        })
    }

    protected readonly analytics: FirebaseAnalyticsReporter;
    private _lastFirebaseToken: ILastFirebaseToken | null = null;

    //we need this queue because in the CIS/CIP autologin scenario where the current session is logged out and a new session is logged in
    //we need to execute the subscribe/unsubscribe actions in the right order without overlapping
    private readonly _subscribeUnsubscribeQueue: SubscribeUnsubscribeActionsQueue = new SubscribeUnsubscribeActionsQueue();

    private _triple8ConnectionFactory: Triple8ConnectionFactory | null = null;
    readonly topics: IFirebaseTopics;

    private _getAllTopics(): IFirebaseTopicModel[] {
        return Object.values(this.topics);
    }

    private _getConnectionFactory(): Triple8ConnectionFactory {
        if(!this._triple8ConnectionFactory) {
            this._triple8ConnectionFactory = new Triple8ConnectionFactory(this.services, () => this._createNewFirebaseToken());
        }

        return this._triple8ConnectionFactory;
    }


    private _getConnection = async (): Promise<ITriple8Connection> => {
        const factory = this._getConnectionFactory();
        return await factory.getConnection();
    }

    private  _subscribeToTopicsPromise: Promise<any> | null = null;
    private async _subscribeToTopics(): Promise<void> {

        if(this._subscribeToTopicsPromise) {
            return this._subscribeToTopicsPromise;
        }


        const subscribe = async () => {
            for(let topic of this._getAllTopics()) {
                try {
                    await topic.subscribe();
                } catch (err) {
                    this.services.logger.error(`Failed to subscribe to firebase topic ${topic.getTopicName()}`, err);
                    this.analytics.sendSubscriptionError(topic.getTopicLabel(), JSON.stringify(err));

                }
            }
        }

        this._subscribeToTopicsPromise = subscribe().then((async () => {
            const token = await this._getConnectionFactory().getToken();
            this._lastFirebaseToken = {
                token,
                timestamp: Date.now()
            }
        }));

        return this._subscribeToTopicsPromise;

    }

    private async _unsubscribeFromTopics(): Promise<void> {

        if(!this._subscribeToTopicsPromise) {
            return;
        }


        for(let topic of this._getAllTopics()) {
            try {
                await topic.unsubscribe();
            } catch (err) {
                this.services.logger.error(`Failed to unsubscribe from topic ${topic.constructor.name}`, err);
            }
        }

        try {
            await this._triple8ConnectionFactory?.dispose();
        } catch (err) {
            this.services.logger.error(`Failed to dispose Triple8ConnectionFactory`, err);
        }
        this._subscribeToTopicsPromise = null;
        this._triple8ConnectionFactory = null;
        this._lastFirebaseToken = null;
    }

    async waitForSubscriptionReady(): Promise<void> {

        await this.services.user.waitForAutoLogin();

        if(this.services.user.isAuthorized) {
            this._subscribeUnsubscribeQueue.enqueue(() => this._subscribeToTopics());
            await this._subscribeUnsubscribeQueue.waitForAll();
        }
    }

    private async _createNewFirebaseToken(): Promise<string> {
        const response = await this.services.api.userController.getToken({
            TargetType: 4,
            TargetTypeValue: 'firebase'
        });

        this._lastFirebaseToken = {
            token: response.token,
            timestamp: Date.now()
        }

        return response.token;

    }

    async getToken(): Promise<string> {

        await this.waitForSubscriptionReady();

        if(this._lastFirebaseToken) {
            if(Date.now() - this._lastFirebaseToken.timestamp < 90 * 1000) { //if the initial token is less than 90 seconds old. This is because usually platform tokens expires after 2 minutes.
                return this._lastFirebaseToken.token;
            } else {
                return await this._createNewFirebaseToken();
            }
        }

        return await this._createNewFirebaseToken();
    }
}

class SubscribeUnsubscribeActionsQueue {
    private _queue: Array<() => Promise<void>> = [];

    private _executionPromise: Promise<void> | null = null;

    enqueue(action: () => Promise<void>): void {

        this._queue.push(action);

        if(!this._executionPromise) {
            this._executionPromise = new Promise<void>(async (resolve, reject) => {
                try {
                    while(this._queue.length > 0) {
                        const action = this._queue.shift();
                        await action();
                    }
                    resolve();
                } catch (err) {
                    reject(err);
                } finally {
                    this._executionPromise = null;
                }
            });
        }
    }

    async waitForAll(): Promise<void> {
        if(!this._executionPromise) {
            return;
        }

        await this._executionPromise;

    }
}